import React from 'react';
import { BookOpen, FileText, PlayCircle } from 'lucide-react';
import { SupportingMaterialMarkdown } from './SupportingMaterialMarkdown';
import {
    materialStableKey,
    resolveMaterialsForStep,
    stripDuplicateMaterialTitleFromContent
} from './instructionStepUtils';
import { getDisplayVideoOptions, isVideoRemovedFromYouTube } from './mediaReviewUtils';
import type { InstructionStepDto, SupportingMaterialDto } from './types';
import { LessonVideoPlayer, LessonVideoRemovedPlaceholder } from '../LessonVideoPlayer';
import { YouTubeApiServicesNotice } from '../YouTubeApiServicesNotice';

const MATERIAL_TYPE_LABELS: Record<string, string> = {
    '10': 'Worksheet',
    '20': 'Reading',
    '30': 'Video'
};

function getIcon(typeKey?: string) {
    switch (typeKey) {
        case '10':
            return FileText;
        case '20':
            return BookOpen;
        case '30':
            return PlayCircle;
        default:
            return FileText;
    }
}

interface InstructionStepMaterialsProps {
    step: InstructionStepDto;
    supportingMaterials?: SupportingMaterialDto[];
    lessonTitle?: string;
    lessonSteps?: InstructionStepDto[];
}

export function InstructionStepMaterials({
    step,
    supportingMaterials,
    lessonTitle,
    lessonSteps
}: InstructionStepMaterialsProps) {
    const materials = resolveMaterialsForStep(step, supportingMaterials, lessonSteps);
    if (!materials.length) return null;

    return (
        <div className="mt-8 border-t border-gray-200 pt-6">
            <h6 className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-700">
                Materials for this step
            </h6>
            <div className="space-y-3">
                {materials.map((material, index) => {
                    const Icon = getIcon(material.e251_MaterialType);
                    const typeLabel = MATERIAL_TYPE_LABELS[material.e251_MaterialType ?? ''] ?? 'Material';
                    const isVideo = material.e251_MaterialType === '30';
                    const videoRemoved = isVideo && isVideoRemovedFromYouTube(material);
                    const displayTitle = material.e251_Title || typeLabel;
                    const displayContent = stripDuplicateMaterialTitleFromContent(
                        material.e251_Content || '',
                        material.e251_Title
                    );

                    return (
                        <div
                            key={materialStableKey(material, index)}
                            className="rounded-md border border-gray-200 bg-white p-3"
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <Icon size={14} className="text-blumine shrink-0" />
                                <span className="text-sm font-medium text-gray-900">{displayTitle}</span>
                                <span className="text-xs text-gray-500">{typeLabel}</span>
                            </div>
                            {isVideo ? (
                                <div className="text-sm text-gray-600 space-y-3">
                                    {displayContent && (
                                        <p className="whitespace-pre-wrap">{displayContent}</p>
                                    )}
                                    {videoRemoved ? (
                                        <LessonVideoRemovedPlaceholder compact />
                                    ) : (
                                        <>
                                            {getDisplayVideoOptions(material).map(option => (
                                                <LessonVideoPlayer
                                                    key={option.e251_VideoId ?? option.e251_VideoUrl}
                                                    videoId={option.e251_VideoId}
                                                    title={option.e251_VideoTitle || 'Watch video'}
                                                    channelTitle={option.e251_VideoChannelTitle}
                                                    url={option.e251_VideoUrl}
                                                    embeddable={option.e251_VideoEmbeddable}
                                                    compact
                                                />
                                            ))}
                                            {!material.e251_VideoOptions?.length && material.e251_Url && (
                                                <LessonVideoPlayer
                                                    title={material.e251_Title || 'Watch video'}
                                                    channelTitle={material.e251_VideoChannelTitle}
                                                    url={material.e251_Url}
                                                    compact
                                                />
                                            )}
                                            <YouTubeApiServicesNotice />
                                        </>
                                    )}
                                </div>
                            ) : (
                                <SupportingMaterialMarkdown content={displayContent} />
                            )}
                            {lessonTitle && (
                                <p className="sr-only">Lesson: {lessonTitle}</p>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
